/**
 * ReconHistory — rapprochements reportés du compte (période, solde du relevé, solde GL, écart, écriture d'ajustements) + PDF de chacun
 * + annulation du dernier report.
 */
import { useState } from "react";
import { FileDown, History, Undo2 } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { errMsg, money } from "../../comptabilite/_shared";

export default function ReconHistory({ base, accountId, items, onChanged }) {
  const [busy, setBusy] = useState(null);
  const pdf = async (r) => {
    setBusy(r.id);
    try {
      const res = await api.get(`${base}/bank/reconciliation/pdf`, { params: { account_id: accountId, rec_id: r.id, details: true }, responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `Rapprochement_bancaire_${r.period}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e, "PDF indisponible")); }
    finally { setBusy(null); }
  };
  const unpost = async (r) => {
    if (!window.confirm(`Annuler le report du rapprochement ${r.period} ?\nL'écriture d'ajustements (s'il y en a une) sera annulée ; les états des transactions ne changent pas.`)) return;
    setBusy(r.id);
    try { await api.post(`${base}/bank/reconciliation/unpost`, { account_id: accountId }); toast.success(`Report ${r.period} annulé`); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };
  return (
    <div className="bg-white border rounded-xl overflow-hidden" data-testid="recon-history">
      <div className="px-3 py-2 text-xs font-semibold text-slate-600 inline-flex items-center gap-1.5 border-b w-full"><History className="w-3.5 h-3.5" /> Rapprochements reportés ({items?.length || 0})</div>
      {!items?.length ? <p className="px-3 py-3 text-xs text-slate-400" data-testid="recon-history-empty">Aucun rapprochement reporté pour ce compte — le premier report fige la période et les transactions en circulation.</p> : (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr><th className="text-left px-3 py-1.5">Période</th><th className="text-left px-2 py-1.5">Date</th><th className="text-left px-2 py-1.5">Description</th><th className="text-right px-2 py-1.5">Solde relevé</th><th className="text-right px-2 py-1.5">Solde GL</th><th className="text-right px-2 py-1.5">En circ.</th><th className="text-right px-2 py-1.5">Écart</th><th className="px-2 py-1.5"></th></tr>
          </thead>
          <tbody className="divide-y">
            {items.map((r, idx) => (
              <tr key={r.id} data-testid={`recon-history-${r.id}`}>
                <td className="px-3 py-1.5 font-mono font-semibold">{r.period}</td>
                <td className="px-2 py-1.5 text-slate-600">{r.recon_date}</td>
                <td className="px-2 py-1.5 text-slate-700 truncate max-w-[260px]">{r.description}{r.entry_id && <span className="text-slate-400"> · écriture d'ajustements</span>}{r.forced && <span className="text-rose-600"> · forcé</span>}</td>
                <td className="px-2 py-1.5 text-right font-mono">{money(r.statement_balance)}</td>
                <td className="px-2 py-1.5 text-right font-mono">{money(r.gl_balance)}</td>
                <td className="px-2 py-1.5 text-right">{r.outstanding_count}</td>
                <td className={`px-2 py-1.5 text-right font-mono font-semibold ${Math.abs(r.difference || 0) < 0.005 ? "text-emerald-700" : "text-rose-700"}`}>{money(r.difference)}</td>
                <td className="px-2 py-1.5 text-right whitespace-nowrap">
                  <button onClick={() => pdf(r)} disabled={busy === r.id} className="text-teal-700 hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid={`recon-history-pdf-${r.id}`}><FileDown className="w-3 h-3" /> PDF</button>
                  {idx === 0 && <button onClick={() => unpost(r)} disabled={busy === r.id} className="ml-3 text-slate-500 hover:underline inline-flex items-center gap-1 disabled:opacity-50" title="Annuler le dernier report" data-testid={`recon-history-unpost-${r.id}`}><Undo2 className="w-3 h-3" /> Annuler</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
